import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../../components/buttons/button";
import Dropdown from "../../../../components/dropdown/dropdown";
import { EmptyState } from "../../../../components/empty";
import { EventCard } from "../../../../components/event/eventCard";
import { CaretDown } from "../../../../components/icon/downcaret";
import { SearchInput } from "../../../../components/inputs/searchInput";
import Pagination from "../../../../components/pagination";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useGetAllUserEventsQuery } from "../../../../redux/features/admin/event/events.api";
import { GetAllUserEventsQuery } from "../../../../redux/features/admin/event/events.types";
import { GetSingleUserResponse } from "../../../../redux/features/admin/users/users.types";
import {
  eventStatusPillMap,
  userTypePillMap,
} from "../../../../utils/constants";
import { formatDate } from "../../../../utils/helpers";

export const SingleUserEvents = ({
  user,
  goToEvents = () => {},
  userId,
}: {
  user?: GetSingleUserResponse;
  goToEvents?: () => void;
  userId?: string;
}) => {
  const [query, setQuery] = useState<GetAllUserEventsQuery>({
    page: 1,
    limit: 5,
    search: undefined,
    sortOrder: "desc",
    type: undefined,
    status: undefined,
  });

  const navigate = useNavigate();

  const { data: userEvents, isFetching: fetchingUserEvents } =
    useGetAllUserEventsQuery(
      {
        userId: userId ?? "",
        params: {
          ...query,
          search: query.search === "" ? undefined : query.search,
        },
      },
      {
        refetchOnMountOrArgChange: true,
      },
    );

  return (
    <>
      <BgWrapper>
        <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
          <SearchInput
            searchFunction={(value) => {
              setQuery({
                ...query,
                search: value,
                page: 1,
              });
            }}
            placeholder="Search"
            className="md:!max-w-[224px]"
          />

          <div className="grid w-full min-w-[324px] max-w-fit grid-cols-2 items-center gap-3">
            <Dropdown
              menuItems={[
                {
                  name: "All Events",
                  onClick: () => {
                    setQuery({
                      ...query,
                      status: undefined,
                    });
                  },
                },

                {
                  name: "Upcoming Events",
                  onClick: () => {
                    setQuery({
                      ...query,
                      status: "upcoming",
                    });
                  },
                },
                {
                  name: "Past Events",
                  onClick: () => {
                    setQuery({
                      ...query,
                      status: "completed",
                    });
                  },
                },
                {
                  name: "Draft Events",
                  onClick: () => {
                    setQuery({
                      ...query,
                      status: "draft",
                    });
                  },
                },
              ]}
              position="bottom end"
              menuClassName=""
            >
              <Button
                type="button"
                text={
                  query.status
                    ? `${eventStatusPillMap[query.status]?.text?.toLowerCase()} Events`
                    : "All Events"
                }
                className="!min-w-fit !justify-between !rounded-2xl !p-3 !text-sm !capitalize"
                variation="plain-grey"
                extra={<CaretDown width={16} height={16} />}
                disabled={false}
                loading={false}
              />
            </Dropdown>

            <Dropdown
              menuItems={[
                {
                  name: "All Users",
                  onClick: () => {
                    setQuery({
                      ...query,
                      type: undefined,
                    });
                  },
                },

                {
                  name: "Organizer",
                  onClick: () => {
                    setQuery({
                      ...query,
                      type: "organiser",
                    });
                  },
                },
                {
                  name: "Guest",
                  onClick: () => {
                    setQuery({
                      ...query,
                      type: "guest",
                    });
                  },
                },
              ]}
              position="bottom end"
              menuClassName=""
            >
              <Button
                type="button"
                text={
                  query.type
                    ? `${userTypePillMap[query.type]?.text?.toLowerCase()}`
                    : "All Users"
                }
                className="!min-w-fit !justify-between !rounded-2xl !p-3 !text-sm !capitalize"
                variation="plain-grey"
                extra={<CaretDown width={16} height={16} />}
                disabled={false}
                loading={false}
              />
            </Dropdown>
          </div>
        </div>
      </BgWrapper>

      {fetchingUserEvents
        ? [1, 2].map((v) => {
            return <BgWrapper key={`${v}`} loaderHeight={184} loading />;
          })
        : undefined}

      {!fetchingUserEvents &&
      [0, undefined].includes(userEvents?.data?.data?.length) ? (
        <BgWrapper>
          <EmptyState
            text="No Events Yet"
            subtext="This user hasn’t created an event yet"
            image="/assets/images/calendar-3d.png"
          />
        </BgWrapper>
      ) : undefined}

      {!fetchingUserEvents &&
      ![0, undefined].includes(userEvents?.data?.data?.length)
        ? userEvents?.data?.data?.map((event, idx) => {
            return (
              <EventCard
                key={`${idx}-${event._id}`}
                image={event.image}
                name={event.name}
                location={event.location}
                startDate={formatDate(event.startDate, "ddd, MMMM DD, YYYY")}
                endDate={formatDate(event.endDate, "ddd, MMMM DD, YYYY")}
                startTime={event.startTime}
                endTime={event.endTime}
                noTickets={event.ticketCount}
                totalGuests={undefined}
                isOrganizer={event.userType === "organiser"}
                event={event}
                imageDimension={158}
                imageClassName="!order-last"
                titleClassName="!text-[24px]"
                className="xl:!items-start"
                clickable
                onClick={() => {
                  if (event.userType === "guest") {
                    navigate(`/events/${event._id}/guest`);
                  } else {
                    navigate(`/events/${event._id}`);
                  }
                }}
                showStatusPill
              />
            );
          })
        : undefined}

      <BgWrapper>
        <Pagination
          currentPage={query.page!}
          totalCount={userEvents?.data?.totalCount ?? 0}
          itemsPerPage={query.limit!}
          onPageChange={(page) => {
            setQuery({
              ...query,
              page: page,
            });
          }}
          containerClassName="!px-0 !py-0"
          showPagNumbers={false}
        />
      </BgWrapper>
    </>
  );
};
